import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog, Button, Box, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, CircularProgress, Chip, IconButton, Tooltip, Switch, FormControlLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';

// Icons
import CloseIcon from '@material-ui/icons/Close';
import SettingsIcon from '@material-ui/icons/Settings';
import BusinessIcon from '@material-ui/icons/Business';
import PublicIcon from '@material-ui/icons/Public';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import StarIcon from '@material-ui/icons/Star';
import RoomIcon from '@material-ui/icons/Room';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import EditIcon from '@material-ui/icons/Edit';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import NotificationRecipients from './NotificationRecipients';
import HistoryIcon from '@material-ui/icons/History';
import PersonIcon from '@material-ui/icons/Person';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

import { toast } from 'react-toastify';
import { AuthContext } from '../../App';
import { CURRENCIES } from '../../utils/currencies';
import { TIMEZONES, COUNTRIES, WEEKDAYS } from '../../utils/localeOptions';
import { selectLocations, fetchLocations } from '../../store/slices/locationsSlice';
import { setActiveView } from '../../store/slices/uiSlice';
import { isMoveExpressTenant } from '../../utils/manageHelpers';
import { t } from '../../i18n';

// Tipo do favorito (enum LocationType) → chave i18n, para o selo aparecer
// traduzido ("Armazém", "Local de Interesse"...) em vez do valor cru ("warehouse", "poi").
// Mesmas chaves usadas no "Gerir favoritos" (ManageFavoritesDialog).
const LOC_TYPE_LABEL_KEY = {
  warehouse: 'typeWarehouse',
  safe_point: 'markerSafe',
  overnight: 'markerOvernight',
  fuel: 'markerFuel',
  poi: 'markerPoi',
  rest_area: 'typeRestArea',
  garage: 'typeGarage',
  other: 'typeOther',
};
const locTypeLabel = (lang, type) => {
  const key = LOC_TYPE_LABEL_KEY[type];
  return key ? t(lang, key) : (type || '—');
};

const VEHICLE_TYPES = [
  { value: 'cisterna',      label: 'Camião Cisterna',     weight: '40 t' },
  { value: 'frigorifico',   label: 'Camião Frigorífico',  weight: '26 t' },
  { value: 'light_truck',   label: 'Camião Ligeiro',      weight: '7,5 t' },
  { value: 'general_cargo', label: 'Camião Mercadorias',  weight: '24 t' },
  { value: 'car',           label: 'Carro Ligeiro',       weight: '2,5 t' },
];

const SECTIONS = [
  { key: 'profile',  label: 'Perfil',    icon: BusinessIcon,      description: 'Identificação e tipos de operação' },
  { key: 'general',  label: 'Geral',     icon: PublicIcon,        description: 'Moeda, país, idioma e fuso horário' },
  { key: 'routes',   label: 'Rotas',     icon: LocalShippingIcon, description: 'Cálculo de rotas e dimensões do veículo' },
  { key: 'fuel',     label: 'Combustível', icon: LocalGasStationIcon, description: 'Preços por país (sobrepõem EU Oil Bulletin)' },
  { key: 'chat',     label: 'Chat',      icon: ChatBubbleOutlineIcon, description: 'Atalhos de resposta rápida e notificações' },
  { key: 'notifications', label: 'Notificações', icon: NotificationsActiveIcon, description: 'Responsáveis que recebem email/SMS por marco' },
  { key: 'favorites',label: 'Favoritos', icon: StarIcon,          description: 'Locais guardados para reutilização' },
];

// O planner só expõe o essencial do dia-a-dia. A configuração estrutural
// completa (identidade do perfil, tipos de OT, preços de combustível, chat,
// variante de UI, módulos) vive no Perfil, no backoffice. Ver AdminProfiles.
const PLANNER_SECTION_KEYS = ['general', 'routes', 'fuel', 'favorites'];
const PLANNER_SECTIONS = SECTIONS.filter(s => PLANNER_SECTION_KEYS.includes(s.key));

// Países com moeda local detectada automaticamente. Lista alargada cobre
// toda a Europa + África relevante (lusófona + chave para expansão).
// A bandeira é gerada a partir do ISO-2 via fromCodePoint.
const FUEL_COUNTRIES = [
  // ─── Europa Ocidental & Norte ────────────────────────────
  { code: 'PT', name: 'Portugal',          currency: 'EUR', region: 'Europa' },
  { code: 'ES', name: 'Espanha',           currency: 'EUR', region: 'Europa' },
  { code: 'FR', name: 'França',            currency: 'EUR', region: 'Europa' },
  { code: 'DE', name: 'Alemanha',          currency: 'EUR', region: 'Europa' },
  { code: 'IT', name: 'Itália',            currency: 'EUR', region: 'Europa' },
  { code: 'BE', name: 'Bélgica',           currency: 'EUR', region: 'Europa' },
  { code: 'NL', name: 'Países Baixos',     currency: 'EUR', region: 'Europa' },
  { code: 'LU', name: 'Luxemburgo',        currency: 'EUR', region: 'Europa' },
  { code: 'AT', name: 'Áustria',           currency: 'EUR', region: 'Europa' },
  { code: 'IE', name: 'Irlanda',           currency: 'EUR', region: 'Europa' },
  { code: 'GB', name: 'Reino Unido',       currency: 'GBP', region: 'Europa' },
  { code: 'CH', name: 'Suíça',             currency: 'CHF', region: 'Europa' },
  { code: 'DK', name: 'Dinamarca',         currency: 'DKK', region: 'Europa' },
  { code: 'SE', name: 'Suécia',            currency: 'SEK', region: 'Europa' },
  { code: 'NO', name: 'Noruega',           currency: 'NOK', region: 'Europa' },
  { code: 'FI', name: 'Finlândia',         currency: 'EUR', region: 'Europa' },
  { code: 'IS', name: 'Islândia',          currency: 'ISK', region: 'Europa' },
  // ─── Europa Central & Leste ──────────────────────────────
  { code: 'PL', name: 'Polónia',           currency: 'PLN', region: 'Europa' },
  { code: 'CZ', name: 'Chéquia',           currency: 'CZK', region: 'Europa' },
  { code: 'SK', name: 'Eslováquia',        currency: 'EUR', region: 'Europa' },
  { code: 'HU', name: 'Hungria',           currency: 'HUF', region: 'Europa' },
  { code: 'RO', name: 'Roménia',           currency: 'RON', region: 'Europa' },
  { code: 'BG', name: 'Bulgária',          currency: 'BGN', region: 'Europa' },
  { code: 'GR', name: 'Grécia',            currency: 'EUR', region: 'Europa' },
  { code: 'HR', name: 'Croácia',           currency: 'EUR', region: 'Europa' },
  { code: 'SI', name: 'Eslovénia',         currency: 'EUR', region: 'Europa' },
  { code: 'EE', name: 'Estónia',           currency: 'EUR', region: 'Europa' },
  { code: 'LV', name: 'Letónia',           currency: 'EUR', region: 'Europa' },
  { code: 'LT', name: 'Lituânia',          currency: 'EUR', region: 'Europa' },
  { code: 'MT', name: 'Malta',             currency: 'EUR', region: 'Europa' },
  { code: 'CY', name: 'Chipre',            currency: 'EUR', region: 'Europa' },
  { code: 'RS', name: 'Sérvia',            currency: 'RSD', region: 'Europa' },
  // ─── África Lusófona ─────────────────────────────────────
  { code: 'AO', name: 'Angola',            currency: 'AOA', region: 'África' },
  { code: 'MZ', name: 'Moçambique',        currency: 'MZN', region: 'África' },
  { code: 'CV', name: 'Cabo Verde',        currency: 'CVE', region: 'África' },
  { code: 'ST', name: 'São Tomé e Príncipe', currency: 'STN', region: 'África' },
  { code: 'GW', name: 'Guiné-Bissau',      currency: 'XOF', region: 'África' },
  { code: 'GQ', name: 'Guiné Equatorial',  currency: 'XAF', region: 'África' },
  // ─── África Austral ──────────────────────────────────────
  { code: 'ZA', name: 'África do Sul',     currency: 'ZAR', region: 'África' },
  { code: 'NA', name: 'Namíbia',           currency: 'NAD', region: 'África' },
  { code: 'BW', name: 'Botsuana',          currency: 'BWP', region: 'África' },
  { code: 'ZM', name: 'Zâmbia',            currency: 'ZMW', region: 'África' },
  { code: 'ZW', name: 'Zimbabué',          currency: 'ZWL', region: 'África' },
  { code: 'MW', name: 'Malawi',            currency: 'MWK', region: 'África' },
  { code: 'MU', name: 'Maurícia',          currency: 'MUR', region: 'África' },
  { code: 'MG', name: 'Madagáscar',        currency: 'MGA', region: 'África' },
  // ─── África Central ──────────────────────────────────────
  { code: 'CM', name: 'Camarões',          currency: 'XAF', region: 'África' },
  { code: 'GA', name: 'Gabão',             currency: 'XAF', region: 'África' },
  { code: 'CG', name: 'Congo',             currency: 'XAF', region: 'África' },
  { code: 'CD', name: 'RD Congo',          currency: 'CDF', region: 'África' },
  { code: 'RW', name: 'Ruanda',            currency: 'RWF', region: 'África' },
  // ─── África Ocidental ────────────────────────────────────
  { code: 'NG', name: 'Nigéria',           currency: 'NGN', region: 'África' },
  { code: 'GH', name: 'Gana',              currency: 'GHS', region: 'África' },
  { code: 'CI', name: 'Costa do Marfim',   currency: 'XOF', region: 'África' },
  { code: 'SN', name: 'Senegal',           currency: 'XOF', region: 'África' },
  { code: 'ML', name: 'Mali',              currency: 'XOF', region: 'África' },
  { code: 'BF', name: 'Burquina Faso',     currency: 'XOF', region: 'África' },
  // ─── África Oriental ─────────────────────────────────────
  { code: 'KE', name: 'Quénia',            currency: 'KES', region: 'África' },
  { code: 'TZ', name: 'Tanzânia',          currency: 'TZS', region: 'África' },
  { code: 'UG', name: 'Uganda',            currency: 'UGX', region: 'África' },
  { code: 'ET', name: 'Etiópia',           currency: 'ETB', region: 'África' },
  // ─── Norte de África ─────────────────────────────────────
  { code: 'MA', name: 'Marrocos',          currency: 'MAD', region: 'África' },
  { code: 'DZ', name: 'Argélia',           currency: 'DZD', region: 'África' },
  { code: 'TN', name: 'Tunísia',           currency: 'TND', region: 'África' },
  { code: 'EG', name: 'Egipto',            currency: 'EGP', region: 'África' },
  { code: 'LY', name: 'Líbia',             currency: 'LYD', region: 'África' },
];

const CURRENCY_SYMBOLS = {
  EUR: '€', GBP: '£', CHF: 'Fr.', DKK: 'kr', SEK: 'kr', NOK: 'kr', ISK: 'kr',
  PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei', BGN: 'лв', RSD: 'дин',
  AOA: 'Kz', MZN: 'MT', CVE: '$', STN: 'Db', XOF: 'CFA', XAF: 'CFA',
  ZAR: 'R',  NAD: 'N$', BWP: 'P',  ZMW: 'ZK', ZWL: 'Z$', MWK: 'MK',
  MUR: '₨',  MGA: 'Ar', CDF: 'FC', RWF: 'FRw',
  NGN: '₦',  GHS: '₵',  KES: 'KSh',TZS: 'TSh',UGX: 'USh',ETB: 'Br',
  MAD: 'DH', DZD: 'DA', TND: 'DT', EGP: 'E£', LYD: 'LD',
};

function flagFromIso2(code) {
  if (!code || code.length !== 2) return '🌍';
  return String.fromCodePoint(...code.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}

const useStyles = makeStyles(() => ({
  // Temas do ecrã de Preferências (segue o tema da barra): CLÁSSICO = laranja
  // atual (fallbacks nos var()); CLARO/ESCURO = identidade ecooGPS do ui-kit
  // (tokens em ecoogps-tokens.css). Aplicado por data-pf-theme no root.
  '@global': {
    '[data-pf-theme="light"]': {
      '--pf-root-bg': '#eef3f8',
      '--pf-sidebar-bg': 'linear-gradient(180deg, #ffffff 0%, #f5f8fb 100%)',
      '--pf-line': 'rgba(22,58,96,0.12)',
      '--pf-line-soft': 'rgba(22,58,96,0.08)',
      '--pf-icon-bg': '#ffffff',
      '--pf-accent': '#0677a6',
      '--pf-accent-ico': '#0677a6',
      '--pf-title': '#0b1a2c',
      '--pf-sub': '#3a4e66',
      '--pf-accent-soft': 'rgba(7,150,204,0.08)',
      '--pf-active-border': 'rgba(7,150,204,0.45)',
      '--pf-card-bg': '#ffffff',
      '--pf-text': '#0b1a2c',
      '--pf-text-dim': '#3a4e66',
      '--pf-text-mute': '#8296ad',
      '--pf-footer-bg': '#ffffff',
      '--pf-save-grad': 'linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)',
      '--pf-save-grad-hover': 'linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)',
      '--pf-on-accent': '#03121c',
    },
    '[data-pf-theme="dark"]': {
      '--pf-root-bg': '#08111d',
      '--pf-sidebar-bg': 'linear-gradient(180deg, #0d1828 0%, #0a131f 100%)',
      '--pf-line': 'rgba(110,193,228,0.14)',
      '--pf-line-soft': 'rgba(110,193,228,0.08)',
      '--pf-icon-bg': '#132239',
      '--pf-accent': '#20c6f4',
      '--pf-accent-ico': '#20c6f4',
      '--pf-title': '#eaf3fb',
      '--pf-sub': '#a9bed3',
      '--pf-accent-soft': 'rgba(32,198,244,0.10)',
      '--pf-active-border': 'rgba(32,198,244,0.45)',
      '--pf-card-bg': '#0d1828',
      '--pf-text': '#eaf3fb',
      '--pf-text-dim': '#a9bed3',
      '--pf-text-mute': 'rgba(169,190,211,0.55)',
      '--pf-footer-bg': '#0d1828',
      '--pf-save-grad': 'linear-gradient(90deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)',
      '--pf-save-grad-hover': 'linear-gradient(90deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)',
      '--pf-on-accent': '#03121c',
    },
  },
  paper: {
    borderRadius: 16,
    overflow: 'hidden',
    maxWidth: 1280,
    width: '100%',
    height: '88vh',
    maxHeight: 920,
    boxShadow: '0 24px 60px -12px rgba(15, 23, 42, 0.25)',
  },
  root: {
    display: 'flex',
    height: '100%',
    background: 'var(--pf-root-bg, #f8fafc)',
  },
  // ── Sidebar ──────────────────────────────────────────
  sidebar: {
    width: 250,
    flexShrink: 0,
    background: 'var(--pf-sidebar-bg, linear-gradient(180deg, #fff 0%, #fff7ed 100%))',
    borderRight: '1px solid var(--pf-line, #fed7aa)',
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarHeader: {
    padding: '20px 20px 16px',
    borderBottom: '1px solid var(--pf-line, #fed7aa)',
  },
  sidebarHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  sidebarIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'var(--pf-icon-bg, #fff)',
    border: '1px solid var(--pf-line, #fed7aa)',
    color: 'var(--pf-accent-ico, #ea580c)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sidebarTitle: {
    fontSize: '1.02rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
    color: 'var(--pf-title, #9a3412)',
    lineHeight: 1.2,
  },
  sidebarSub: {
    fontSize: '0.66rem',
    color: 'var(--pf-sub, #c2410c)',
    marginTop: 1,
  },
  clientBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    background: 'var(--pf-accent-soft, #fff7ed)',
    border: '1px solid var(--pf-line, #fed7aa)',
    borderRadius: 10,
    marginTop: 4,
  },
  sidebarNav: {
    padding: '10px 8px',
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 8,
    cursor: 'pointer',
    marginBottom: 2,
    transition: 'all 0.15s',
    border: '1.5px solid transparent',
    '&:hover': { background: 'var(--pf-accent-soft, #fff7ed)' },
  },
  navItemActive: {
    background: 'var(--pf-card-bg, #fff)',
    border: '1.5px solid var(--pf-active-border, #fdba74)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    '&:hover': { background: 'var(--pf-card-bg, #fff)' },
  },
  navItemIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--pf-accent-soft, #fff7ed)',
    color: 'var(--pf-accent-ico, #ea580c)',
    flexShrink: 0,
    transition: 'all 0.15s',
  },
  navItemIconActive: {
    background: 'var(--pf-accent, #f97316)',
    border: '1px solid var(--pf-accent, #f97316)',
    color: 'var(--pf-on-accent, #fff)',
  },
  navItemLabel: {
    fontSize: '0.82rem',
    fontWeight: 500,
    color: 'var(--pf-text, #334155)',
  },
  navItemLabelActive: {
    color: 'var(--pf-title, #9a3412)',
    fontWeight: 600,
  },
  navItemDesc: {
    fontSize: '0.62rem',
    color: 'var(--pf-text-mute, #94a3b8)',
    marginTop: 1,
  },
  // ── Content area ─────────────────────────────────────
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
  },
  contentHeader: {
    padding: '18px 24px 14px',
    background: 'var(--pf-accent-soft, linear-gradient(135deg, #fff7ed 0%, #ffffff 60%))',
    borderBottom: '1px solid var(--pf-line, #fed7aa)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  contentTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
    color: 'var(--pf-title, #9a3412)',
  },
  contentSub: {
    fontSize: '0.72rem',
    color: 'var(--pf-sub, #c2410c)',
    marginTop: 2,
  },
  scrollArea: {
    flex: 1,
    overflow: 'auto',
    padding: '20px 24px',
  },
  card: {
    background: 'var(--pf-card-bg, #fff)',
    border: '1px solid var(--pf-line, #fed7aa)',
    borderRadius: 12,
    padding: '16px 18px',
    marginBottom: 14,
  },
  cardTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--pf-title, #9a3412)',
    marginBottom: 3,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  cardDesc: {
    fontSize: '0.7rem',
    color: 'var(--pf-text-dim, #64748b)',
    marginBottom: 14,
    lineHeight: 1.5,
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginBottom: 10,
  },
  fieldRowFull: {
    marginBottom: 10,
  },
  fieldLabel: {
    fontSize: '0.68rem',
    fontWeight: 600,
    color: 'var(--pf-title, #9a3412)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 4,
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid var(--pf-line-soft, #ffedd5)',
    '&:last-child': { borderBottom: 'none' },
  },
  switchLabel: {
    fontSize: '0.82rem',
    color: 'var(--pf-text, #1e293b)',
    fontWeight: 600,
  },
  switchSub: {
    fontSize: '0.64rem',
    color: 'var(--pf-text-mute, #94a3b8)',
  },
  // ── Footer ───────────────────────────────────────────
  footer: {
    padding: '12px 24px',
    background: 'var(--pf-footer-bg, #fff)',
    borderTop: '1px solid var(--pf-line, #fed7aa)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  audit: {
    fontSize: '0.64rem',
    color: 'var(--pf-text-mute, #94a3b8)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  footerActions: {
    display: 'flex',
    gap: 8,
  },
  // Primary — gradiente (laranja no Clássico, gradiente da marca no kit).
  saveBtn: {
    padding: '7px 20px',
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8rem',
    letterSpacing: '-0.005em',
    background: 'var(--pf-save-grad, linear-gradient(135deg, #fb923c, #f97316))',
    color: 'var(--pf-on-accent, #fff)',
    border: 'none',
    boxShadow: '0 2px 6px rgba(0,0,0,0.20)',
    transition: 'background 0.15s, filter 0.15s',
    '&:hover': { background: 'var(--pf-save-grad-hover, linear-gradient(135deg, #f97316, #ea580c))', filter: 'brightness(1.05)' },
    '&.Mui-disabled': { background: 'var(--pf-line, #fed7aa)', color: '#fff', border: 'none', boxShadow: 'none' },
    '& .MuiButton-startIcon svg': { color: 'var(--pf-on-accent, #fff)' },
  },
  // Chip-style toggle for order types
  typeChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 16px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 500,
    transition: 'all 0.15s',
    userSelect: 'none',
  },
}));

export default function PreferencesDialog({ open, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings, clientName, userEmail, userRole, updateClientSettings, clientExternalId } = useContext(AuthContext);
  const canEdit = userRole === 'admin' || userRole === 'super_admin';
  // Configuração ESTRUTURAL/comercial (identidade, tipos de OT, moeda/país/fuso)
  // é gerida no BACKOFFICE da plataforma, não aqui. No planner fica só para
  // CONSULTA (read-only), mesmo para admins da organização — evita duplicação e
  // que a organização altere o que é estrutural. O OPERACIONAL continua editável.
  const canEditStruct = false;
  // Move Express (client_id local 8 / externalId '8028') usa um subconjunto
  // das preferências — escondemos as opções que não fazem sentido para a
  // operação de combustível deles (ver isMoveExpress abaixo).
  const isMoveExpress = isMoveExpressTenant(clientExternalId, clientSettings);
  const locations = useSelector(selectLocations);

  const [section, setSection] = useState('general');
  const [form, setForm] = useState({});
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  // Segue o tema da barra principal (localStorage 'plannerToolbarTheme').
  // classic → laranja (fallbacks); light/dark → identidade ecooGPS do kit.
  const [pfTheme, setPfTheme] = useState('classic');
  useEffect(() => {
    if (!open) return;
    try { setPfTheme(localStorage.getItem('plannerToolbarTheme') || 'classic'); } catch { setPfTheme('classic'); }
  }, [open]);

  useEffect(() => {
    if (open && token && locations.length === 0) {
      dispatch(fetchLocations(token));
    }
  }, [open, token, locations.length, dispatch]);

  useEffect(() => {
    if (!open || !token) return;
    setLoadingProfile(true);
    fetch('/planner-api/clients/me/profile', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(p => setProfile(p))
      .catch(() => setProfile(null))
      .finally(() => setLoadingProfile(false));
  }, [open, token]);

  useEffect(() => {
    if (!open) return;
    const p = profile || {};
    const s = clientSettings || {};
    setForm({
      name: p.name || '',
      isActive: p.isActive !== false,
      allowedOrderTypes: Array.isArray(p.allowedOrderTypes) ? p.allowedOrderTypes : ['delivery', 'assistance'],
      defaultCurrency: p.defaultCurrency || s.defaultCurrency || 'EUR',
      defaultCountry: p.defaultCountry || s.defaultCountry || 'PT',
      timezone: p.timezone || s.timezone || 'Europe/Lisbon',
      defaultLanguage: p.defaultLanguage || s.defaultLanguage || 'pt',
      defaultConsumption: (p.defaultConsumption ?? s.defaultConsumption) ?? '',
      defaultFuelPrice: (p.defaultFuelPrice ?? s.defaultFuelPrice) ?? '',
      useTachograph: (p.useTachograph ?? s.useTachograph) !== false,
      // Live tracking source — determines whether the operator's monitor
      // pulls position from the driver app, the vehicle's Traccar device,
      // or both (auto-prefers the most reliable). Default: 'both'.
      liveTrackingSource: (p.liveTrackingSource || s.liveTrackingSource || 'both'),
      // Horário de trabalho — base da "utilização útil" no dashboard.
      workHoursPerDay: (p.workHoursPerDay ?? s.workHoursPerDay ?? 8),
      workDays: Array.isArray(p.workDays) ? p.workDays
        : (Array.isArray(s.workDays) ? s.workDays : [1, 2, 3, 4, 5]),
      // Routing provider for cars/light vehicles. Default: HERE.
      carRoutingProvider: (p.carRoutingProvider || s.carRoutingProvider || 'here'),
      // Routing provider for trucks/heavy vehicles (cisterna…). Default: HERE.
      truckRoutingProvider: (p.truckRoutingProvider || s.truckRoutingProvider || 'here'),
      // Provider por tipo de veículo (override fino). Vazio = herda dos baldes.
      routingProviderBySubType: (p.routingProviderBySubType ?? s.routingProviderBySubType) || {},
      // Preços de combustível por país. { PT: { diesel: 1.71, gasoline: 1.85 }, AO: { diesel: 300 } }
      // Valores na moeda local de cada país.
      fuelPricesByCountry: (p.fuelPricesByCountry || s.fuelPricesByCountry || {}),
      defaultDepartureByOrderType: (p.defaultDepartureByOrderType || s.defaultDepartureByOrderType || {}),
      crossingFares: (p.crossingFares || s.crossingFares || {}),
      // Fase 2 do chat — atalhos de resposta rápida (até 8) e som de
      // notificação. Default 4 frases neutras + som on.
      chatQuickReplies: Array.isArray(p.chatQuickReplies || s.chatQuickReplies)
        ? (p.chatQuickReplies || s.chatQuickReplies)
        : ['Recebido ✓', 'Cheguei OK?', 'Posso ajudar?', 'Reportar quando concluir'],
      chatNotificationSound: (p.chatNotificationSound ?? s.chatNotificationSound) !== false,
      defaultSpeed: (p.defaultSpeed ?? s.defaultSpeed) ?? '',
      defaultVehicleSubType: p.defaultVehicleSubType || s.defaultVehicleSubType || 'cisterna',
      avoidTolls: !!(p.avoidTolls ?? s.avoidTolls),
      avoidFerries: !!(p.avoidFerries ?? s.avoidFerries),
      avoidMotorways: !!(p.avoidMotorways ?? s.avoidMotorways),
      avoidTunnels: !!(p.avoidTunnels ?? s.avoidTunnels),
      avoidLEZ: !!(p.avoidLEZ ?? s.avoidLEZ),
      defaultFuelType: (p.defaultFuelType ?? s.defaultFuelType) || 'diesel',
      // Default LIGADO — só false quando explicitamente desligado.
      showRouteAlternatives: (p.showRouteAlternatives ?? s.showRouteAlternatives) !== false,
      truckWeight: (p.truckWeight ?? s.truckWeight) ?? '',
      truckHeight: (p.truckHeight ?? s.truckHeight) ?? '',
      truckWidth: (p.truckWidth ?? s.truckWidth) ?? '',
      truckLength: (p.truckLength ?? s.truckLength) ?? '',
      truckAxleLoad: (p.truckAxleLoad ?? s.truckAxleLoad) ?? '',
    });
  }, [open, clientSettings, profile]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    setSaving(true);
    try {
      const body = { ...form };
      ['defaultConsumption', 'defaultFuelPrice', 'defaultSpeed',
        'truckWeight', 'truckHeight', 'truckWidth', 'truckLength', 'truckAxleLoad'
      ].forEach(k => {
        if (body[k] === '' || body[k] == null) delete body[k];
        else body[k] = Number(body[k]);
      });
      if (!body.name || !String(body.name).trim()) delete body.name;
      else body.name = String(body.name).trim();
      if (!Array.isArray(body.allowedOrderTypes)) delete body.allowedOrderTypes;

      const res = await fetch('/planner-api/clients/me/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro a gravar');
      }
      const updated = await res.json();
      setProfile(updated);
      updateClientSettings({
        defaultCurrency: updated.defaultCurrency,
        defaultCountry: updated.defaultCountry,
        timezone: updated.timezone,
        defaultLanguage: updated.defaultLanguage,
        defaultConsumption: updated.defaultConsumption,
        defaultFuelPrice: updated.defaultFuelPrice,
        defaultSpeed: updated.defaultSpeed,
        useTachograph: updated.useTachograph,
        liveTrackingSource: updated.liveTrackingSource,
        workHoursPerDay: updated.workHoursPerDay,
        workDays: updated.workDays,
        carRoutingProvider: updated.carRoutingProvider,
        truckRoutingProvider: updated.truckRoutingProvider,
        routingProviderBySubType: updated.routingProviderBySubType && typeof updated.routingProviderBySubType === 'object' ? updated.routingProviderBySubType : {},
        fuelPricesByCountry: updated.fuelPricesByCountry,
        defaultDepartureByOrderType: updated.defaultDepartureByOrderType,
        crossingFares: updated.crossingFares,
        chatQuickReplies: updated.chatQuickReplies,
        chatNotificationSound: updated.chatNotificationSound,
        defaultVehicleSubType: updated.defaultVehicleSubType,
        avoidTolls: updated.avoidTolls,
        avoidFerries: updated.avoidFerries,
        avoidMotorways: updated.avoidMotorways,
        avoidTunnels: updated.avoidTunnels,
        avoidLEZ: updated.avoidLEZ,
        defaultFuelType: updated.defaultFuelType,
        showRouteAlternatives: updated.showRouteAlternatives,
        truckWeight: updated.truckWeight,
        truckAxleLoad: updated.truckAxleLoad,
        truckHeight: updated.truckHeight,
        truckWidth: updated.truckWidth,
        truckLength: updated.truckLength,
        allowedOrderTypes: updated.allowedOrderTypes,
      });
      toast.success('Preferências guardadas');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  const activeSection = SECTIONS.find(s => s.key === section);
  const ActiveIcon = activeSection?.icon || SettingsIcon;

  // Short client tag derived from email "+tag@" pattern or last word of client name
  const plusMatch = (userEmail || '').match(/\+([^@]+)@/);
  const shortTag = plusMatch ? plusMatch[1] : (clientName ? clientName.trim().split(/\s+/).pop() : '');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ className: classes.paper }}
      BackdropProps={{ style: { background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' } }}
    >
      <Box className={classes.root} data-pf-theme={pfTheme}>
        {/* ════════ SIDEBAR ════════ */}
        <Box className={classes.sidebar}>
          <Box className={classes.sidebarHeader}>
            <Box className={classes.sidebarHeaderRow}>
              <Box className={classes.sidebarIcon}>
                <SettingsIcon style={{ fontSize: 20 }} />
              </Box>
              <Box style={{ minWidth: 0 }}>
                <Typography className={classes.sidebarTitle}>Preferências</Typography>
                <Typography className={classes.sidebarSub}>do cliente</Typography>
              </Box>
            </Box>
            {clientName && (
              <Box className={classes.clientBadge}>
                <Box style={{ width: 6, height: 6, borderRadius: '50%', background: '#64748b' }} />
                <Typography style={{ fontSize: '0.68rem', fontWeight: 500, color: '#475569', textTransform: 'lowercase' }}>
                  {shortTag || clientName}
                </Typography>
                {!canEdit && (
                  <Tooltip title="Não tens permissão para alterar — apenas leitura">
                    <Chip
                      size="small" label="Leitura"
                      style={{ height: 18, fontSize: '0.56rem', marginLeft: 'auto', background: '#f1f5f9', color: '#475569' }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
          <Box className={classes.sidebarNav}>
            {(isMoveExpress
              ? [...PLANNER_SECTIONS.filter(s => s.key !== 'favorites'),
                 SECTIONS.find(s => s.key === 'notifications'),
                 PLANNER_SECTIONS.find(s => s.key === 'favorites')].filter(Boolean)
              : PLANNER_SECTIONS
            ).map(s => {
              const Icon = s.icon;
              const isActive = section === s.key;
              const isFavorites = s.key === 'favorites';
              return (
                <Box
                  key={s.key}
                  onClick={() => setSection(s.key)}
                  className={`${classes.navItem} ${isActive ? classes.navItemActive : ''}`}
                >
                  <Box className={`${classes.navItemIcon} ${isActive ? classes.navItemIconActive : ''}`}>
                    <Icon style={{ fontSize: 16 }} />
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography className={`${classes.navItemLabel} ${isActive ? classes.navItemLabelActive : ''}`}>
                      {s.label}
                      {isFavorites && locations.length > 0 && (
                        <Typography component="span" style={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 600, marginLeft: 6 }}>
                          · {locations.length}
                        </Typography>
                      )}
                    </Typography>
                    <Typography className={classes.navItemDesc}>{s.description}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ════════ CONTENT ════════ */}
        <Box className={classes.content}>
          {/* Header */}
          <Box className={classes.contentHeader}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <Box style={{
                width: 32, height: 32, borderRadius: 10,
                background: '#f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ActiveIcon style={{ fontSize: 18, color: '#475569' }} />
              </Box>
              <Box>
                <Typography className={classes.contentTitle}>{activeSection?.label}</Typography>
                <Typography className={classes.contentSub}>{activeSection?.description}</Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon style={{ fontSize: 20, color: '#64748b' }} />
            </IconButton>
          </Box>

          {/* Scrollable content */}
          <Box className={classes.scrollArea}>
            {/* ═════ Profile ═════ */}
            {section === 'profile' && (
              <>
                {loadingProfile && (
                  <Box style={{ textAlign: 'center', padding: 30 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {/* Aviso: esta secção é estrutural — gerida no backoffice. */}
                <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', marginBottom: 14, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  <span style={{ fontSize: 15 }}>🔒</span>
                  <Typography style={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.45 }}>
                    <b>Configuração estrutural</b> — a identidade do perfil e os tipos de operação são definidos no <b>backoffice da plataforma</b>. Aqui ficam apenas para <b>consulta</b>.
                  </Typography>
                </Box>

                {/* Identification */}
                <Box className={classes.card}>
                  <Typography className={classes.cardTitle}>
                    <BusinessIcon style={{ fontSize: 16, color: '#475569' }} />
                    Identificação
                  </Typography>
                  <Typography className={classes.cardDesc}>
                    Nome e estado do perfil. O nome identifica o tipo de negócio do cliente
                    (distribuição, assistência técnica, etc.) e é usado em relatórios.
                  </Typography>
                  <Box className={classes.fieldRow}>
                    <Box>
                      <Typography className={classes.fieldLabel}>Nome do perfil</Typography>
                      <TextField
                        fullWidth variant="outlined" size="small"
                        placeholder="Ex.: Petrotec, Transfull, Casais"
                        value={form.name || ''}
                        onChange={e => setField('name', e.target.value)}
                        disabled={!canEditStruct}
                      />
                    </Box>
                    <Box>
                      <Typography className={classes.fieldLabel}>Estado</Typography>
                      <Box style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 12px', borderRadius: 8,
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        height: 40,
                      }}>
                        <Switch
                          checked={!!form.isActive}
                          onChange={e => setField('isActive', e.target.checked)}
                          disabled={!canEditStruct}
                          color="default"
                          size="small"
                        />
                        <Box style={{ width: 8, height: 8, borderRadius: '50%', background: form.isActive ? '#22c55e' : '#94a3b8' }} />
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 500, color: '#0f172a' }}>
                          {form.isActive ? 'Ativo' : 'Desativado'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Order types */}
                <Box className={classes.card}>
                  <Typography className={classes.cardTitle}>
                    <LocalShippingIcon style={{ fontSize: 16, color: '#475569' }} />
                    Tipos de ordem permitidos
                  </Typography>
                  <Typography className={classes.cardDesc}>
                    Controla quais os tipos de OT que este cliente pode criar. Os operadores
                    só verão no formulário de nova OT os tipos que estiverem ativados aqui.
                  </Typography>
                  <Box style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {[
                      // Soft palette: dot indicator carries the semantic colour,
                      // active state is just a tinted slate background.
                      { key: 'delivery',   label: '📦 Entrega de mercadoria', dot: '#3b82f6' },
                      { key: 'assistance', label: '🔧 Assistência técnica',   dot: '#16a34a' },
                      { key: 'fuel',       label: '⛽ Distribuição combustível', dot: '#f97316' },
                    ].filter(t => !isMoveExpress || t.key === 'fuel').map(t => {
                      const active = Array.isArray(form.allowedOrderTypes) && form.allowedOrderTypes.includes(t.key);
                      return (
                        <Box
                          key={t.key}
                          className={classes.typeChip}
                          onClick={() => {
                            if (!canEditStruct) return;
                            const cur = Array.isArray(form.allowedOrderTypes) ? form.allowedOrderTypes : [];
                            setField('allowedOrderTypes', active ? cur.filter(x => x !== t.key) : [...cur, t.key]);
                          }}
                          style={{
                            background: active ? '#f8fafc' : '#fff',
                            color: active ? '#0f172a' : '#64748b',
                            border: `1px solid ${active ? '#94a3b8' : '#e2e8f0'}`,
                            boxShadow: 'none',
                            opacity: canEditStruct ? 1 : 0.65,
                          }}
                        >
                          <Box style={{ width: 8, height: 8, borderRadius: '50%', background: t.dot }} />
                          {t.label}
                          {active && <CheckCircleIcon style={{ fontSize: 15, color: '#64748b' }} />}
                        </Box>
                      );
                    })}
                  </Box>
                  {(!form.allowedOrderTypes || form.allowedOrderTypes.length === 0) && (
                    <Box style={{
                      marginTop: 10, padding: '8px 12px',
                      background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <InfoOutlinedIcon style={{ fontSize: 15, color: '#dc2626' }} />
                      <Typography style={{ fontSize: '0.72rem', color: '#991b1b', fontWeight: 600 }}>
                        Sem tipos ativos — nenhum operador poderá criar OTs.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Audit */}
                {profile && (
                  <Box className={classes.card}>
                    <Typography className={classes.cardTitle}>
                      <HistoryIcon style={{ fontSize: 16, color: '#475569' }} />
                      Histórico
                    </Typography>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.74rem', color: '#475569' }}>
                        <PersonIcon style={{ fontSize: 15, color: '#94a3b8' }} />
                        <span>Criado em <b>{profile.createdAt ? new Date(profile.createdAt).toLocaleString('pt-PT') : '—'}</b></span>
                        {profile.createdBy?.name && <span style={{ color: '#94a3b8' }}>por {profile.createdBy.name}</span>}
                      </Box>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.74rem', color: '#475569' }}>
                        <HistoryIcon style={{ fontSize: 15, color: '#94a3b8' }} />
                        <span>Última alteração em <b>{profile.updatedAt ? new Date(profile.updatedAt).toLocaleString('pt-PT') : '—'}</b></span>
                        {profile.updatedBy?.name && <span style={{ color: '#94a3b8' }}>por {profile.updatedBy.name}</span>}
                      </Box>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* ═════ Geral ═════ */}
            {section === 'general' && (
              <>
              <Box className={classes.card}>
                <Typography className={classes.cardTitle}>
                  <PublicIcon style={{ fontSize: 16, color: '#475569' }} />
                  Localização e idioma
                </Typography>
                <Typography className={classes.cardDesc}>
                  Definições regionais aplicadas por defeito a todas as rotas e documentos
                  gerados para este cliente.
                </Typography>
                <Typography style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.45, marginBottom: 10 }}>
                  🔒 <b>Moeda, país e fuso</b> são estruturais (geridos no backoffice da plataforma, só consulta). O <b>idioma</b> é livre.
                </Typography>
                <Box className={classes.fieldRow}>
                  <Box>
                    <Typography className={classes.fieldLabel}>Moeda por defeito</Typography>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Select value={form.defaultCurrency || 'EUR'} onChange={e => setField('defaultCurrency', e.target.value)} disabled={!canEditStruct}>
                        {CURRENCIES.map(c => (
                          <MenuItem key={c.code} value={c.code}>
                            {c.flag} {c.code} — {c.label} ({c.symbol})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography className={classes.fieldLabel}>País por defeito</Typography>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Select value={form.defaultCountry || 'PT'} onChange={e => setField('defaultCountry', e.target.value)} disabled={!canEditStruct}>
                        {COUNTRIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography className={classes.fieldLabel}>Fuso horário</Typography>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Select value={form.timezone || 'Europe/Lisbon'} onChange={e => setField('timezone', e.target.value)} disabled={!canEditStruct}>
                        {TIMEZONES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography className={classes.fieldLabel}>Idioma por defeito</Typography>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Select value={form.defaultLanguage || 'pt'} onChange={e => setField('defaultLanguage', e.target.value)} disabled={!canEdit}>
                        <MenuItem value="pt">🇵🇹 Português</MenuItem>
                        <MenuItem value="en">🇬🇧 English</MenuItem>
                        <MenuItem value="fr">🇫🇷 Français</MenuItem>
                        <MenuItem value="es">🇪🇸 Español</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              {/* Horário de trabalho — base da "utilização útil" no dashboard */}
              <Box className={classes.card}>
                <Typography className={classes.cardTitle}>
                  🕒 Horário de trabalho
                </Typography>
                <Typography className={classes.cardDesc}>
                  Quando a frota pode trabalhar. É a base da "utilização útil" no dashboard —
                  sem isto, a utilização seria medida contra 24h/dia (noites e fins-de-semana
                  incluídos) e daria sempre perto de 0%. Os feriados do país escolhido acima
                  são excluídos automaticamente.
                </Typography>
                <Box className={classes.fieldRow}>
                  <Box>
                    <Typography className={classes.fieldLabel}>Horas por dia</Typography>
                    <TextField
                      type="number" variant="outlined" size="small" fullWidth
                      value={form.workHoursPerDay ?? 8}
                      disabled={!canEdit}
                      inputProps={{ min: 1, max: 24, step: 0.5 }}
                      onChange={e => setField('workHoursPerDay', e.target.value === '' ? '' : Number(e.target.value))}
                    />
                  </Box>
                </Box>
                <Typography className={classes.fieldLabel} style={{ marginTop: 12 }}>Dias de trabalho</Typography>
                <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                  {WEEKDAYS.map(d => {
                    const on = (form.workDays || [1, 2, 3, 4, 5]).includes(d.value);
                    return (
                      <Box
                        key={d.value}
                        onClick={() => {
                          if (!canEdit) return;
                          const cur = form.workDays || [1, 2, 3, 4, 5];
                          setField('workDays', on ? cur.filter(x => x !== d.value) : [...cur, d.value].sort((a, b) => a - b));
                        }}
                        style={{
                          padding: '8px 14px', borderRadius: 10,
                          cursor: canEdit ? 'pointer' : 'default',
                          fontSize: '0.8rem', fontWeight: 700,
                          background: on ? '#eef2ff' : '#fff',
                          border: `1.5px solid ${on ? '#6366f1' : '#e2e8f0'}`,
                          color: on ? '#4338ca' : '#64748b',
                          transition: 'all 0.15s',
                        }}>
                        {d.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              </>
            )}

            {/* ═════ Rotas ═════ */}
            {section === 'routes' && (
              <>
                {/* Fuel + speed */}
                <Box className={classes.card}>
                  <Typography className={classes.cardTitle}>
                    ⛽ Consumo e velocidade
                  </Typography>
                  <Typography className={classes.cardDesc}>
                    Valores base usados no cálculo de custo e tempo de rota. Podem ser
                    substituídos por rota individualmente.
                  </Typography>
                  <Box className={classes.fieldRow}>
                    <Box>
                      <Typography className={classes.fieldLabel}>Consumo (L ou kWh / 100 km)</Typography>
                      <TextField
                        type="number" size="small" variant="outlined" fullWidth
                        value={form.defaultConsumption}
                        onChange={e => setField('defaultConsumption', e.target.value)}
                        disabled={!canEdit}
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                    </Box>
                    <Box>
                      <Typography className={classes.fieldLabel}>Velocidade média (km/h)</Typography>
                      <TextField
                        type="number" size="small" variant="outlined" fullWidth
                        value={form.defaultSpeed}
                        onChange={e => setField('defaultSpeed', e.target.value)}
                        disabled={!canEdit}
                        inputProps={{ min: 0, step: 1 }}
                      />
                    </Box>
                    {!isMoveExpress && (
                    <Box style={{ display: 'flex', alignItems: 'flex-end', padding: '0 8px' }}>
                      <Box className={classes.switchRow} style={{ width: '100%', padding: 0, borderBottom: 'none' }}>
                        <Box>
                          <Typography className={classes.switchLabel}>Usa tacógrafo</Typography>
                          <Typography className={classes.switchSub}>EU 561/2006 — condução/descanso obrigatórios</Typography>
                        </Box>
                        <Switch
                          checked={!!form.useTachograph}
                          onChange={e => setField('useTachograph', e.target.checked)}
                          disabled={!canEdit}
                          color="primary"
                        />
                      </Box>
                    </Box>
                    )}
                  </Box>
                </Box>

                {/* Fonte de localização em tempo real — REMOVIDO do planner.
                    A escolha (app / Traccar / ambos) é estrutural e vive só no
                    Perfil, no backoffice. O valor continua a ser respeitado. */}

                {/* Seletor de motor de rota (HERE/Google/Híbrido) —
                    REMOVIDO do planner. É config de SETUP com custo
                    associado (Híbrido = 2x API): vive só no Perfil, no
                    backoffice (Rotas → "Rota (ligeiros)"). O valor
                    continua a ser respeitado no cálculo de rotas. */}

                {/* Tutorial: como funciona a detecção de incidentes
                    no Planner. Cobre as 2 fontes (HERE + TomTom), o
                    comportamento agregado no mapa, e a validação
                    pós-cálculo que avisa quando a rota atravessa
                    obras/acidentes. Educacional — fica sempre visível
                    nas Preferências para o operador consultar. */}
                <Box className={classes.card} style={{ background: 'linear-gradient(180deg, #f0f9ff 0%, #fff 100%)', border: '1px solid #bae6fd', display: isMoveExpress ? 'none' : undefined }}>
                  <Typography className={classes.cardTitle} style={{ color: '#075985' }}>
                    📡 Como funciona a detecção de incidentes nas rotas
                  </Typography>
                  <Typography className={classes.cardDesc}>
                    Tutorial · O Planner combina <b>duas fontes de tráfego em tempo real</b> para
                    mostrar acidentes, vias cortadas e obras nas rotas. Esta secção é apenas
                    informativa — não há nada para configurar aqui.
                  </Typography>

                  {/* Fontes de informação */}
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', marginTop: 8, marginBottom: 6 }}>
                    Fontes de tráfego
                  </Typography>
                  <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginBottom: 12 }}>
                    <Box style={{ padding: '8px 10px', borderRadius: 8, background: '#fff', border: '1px solid #e2e8f0' }}>
                      <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: '#0ea5e9', padding: '2px 6px', borderRadius: 4, letterSpacing: '0.04em' }}>HERE</span>
                        <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#0f172a' }}>HERE Traffic v7</Typography>
                      </Box>
                      <Typography style={{ fontSize: '0.62rem', color: '#64748b', lineHeight: 1.45 }}>
                        Boa cobertura em Lisboa centro, principais auto-estradas (A1, A2, A22)
                        e Madrid/Barcelona. Mais fraca em zonas inter-urbanas e cidades médias.
                      </Typography>
                    </Box>
                    <Box style={{ padding: '8px 10px', borderRadius: 8, background: '#fff', border: '1px solid #e2e8f0' }}>
                      <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: '#dc2626', padding: '2px 6px', borderRadius: 4, letterSpacing: '0.04em' }}>TOMTOM</span>
                        <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#0f172a' }}>TomTom Traffic v5</Typography>
                      </Box>
                      <Typography style={{ fontSize: '0.62rem', color: '#64748b', lineHeight: 1.45 }}>
                        Cobertura complementar — frequentemente apanha obras e cortes em
                        rotas regionais (Mafra, Ericeira, IC19, etc.) que o HERE não tem.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Comportamento no mapa */}
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                    Comportamento no mapa (botão ⚠ "Mostrar incidentes")
                  </Typography>
                  <Box style={{ padding: '8px 10px', borderRadius: 8, background: '#fafbfc', border: '1px solid #e2e8f0', marginBottom: 12 }}>
                    <Typography style={{ fontSize: '0.66rem', color: '#475569', lineHeight: 1.5 }}>
                      • Activar o botão chama as <b>duas fontes em paralelo</b> para a área visível do mapa<br />
                      • Eventos são <b>agregados e deduplicados</b> por proximidade (~55 m)<br />
                      • Cada marcador no mapa mostra um <b>badge HERE ou TOMTOM</b> a indicar a fonte<br />
                      • Quando o mesmo incidente é reportado pelos dois, aparece um badge verde adicional <b>"✓ confirmado por TOMTOM/HERE"</b> — sinal de alta confiança<br />
                      • Os ícones distinguem o tipo: 🚨 acidente · 🟠 congestionamento · 🚧 obras · ⛔ via cortada · ⚠️ outros<br />
                      • O mapa re-pede automaticamente os incidentes ao arrastar/zoom (debounce 500 ms)
                    </Typography>
                  </Box>

                  {/* Validação pós-cálculo */}
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                    Validação automática da rota (sem clicar em nada)
                  </Typography>
                  <Box style={{ padding: '8px 10px', borderRadius: 8, background: '#fef9c3', border: '1px solid #fde047', marginBottom: 12 }}>
                    <Typography style={{ fontSize: '0.66rem', color: '#713f12', lineHeight: 1.5 }}>
                      Sempre que clicar em <b>"Calcular Rota"</b> no planeamento, o sistema:<br />
                      <span style={{ display: 'inline-block', marginTop: 4 }}>
                        1. Cria um <b>corredor de 500 m</b> à volta do trajecto calculado<br />
                        2. Procura incidentes <b>HERE + TomTom</b> dentro desse corredor<br />
                        3. Filtra apenas os <b>críticos</b>: vias fechadas, acidentes, obras severas<br />
                        4. Se encontrar algum, aparece um <b>badge vermelho pulsante</b> "⚠ N incidentes" no cabeçalho da rota<br />
                        5. Click expande a lista com tipo, fonte, distância à rota e horários<br />
                        6. Cada incidente tem um botão <b>"↻ Recalcular evitando esta zona"</b> que reformula a rota contornando uma bbox ~600 m à volta do incidente (usa HERE forçado — único provider que aceita avoid de coordenadas arbitrárias)
                      </span>
                    </Typography>
                  </Box>

                  {/* Monitor server-side para rotas em trânsito */}
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                    Monitor server-side para rotas em curso
                  </Typography>
                  <Box style={{ padding: '8px 10px', borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 12 }}>
                    <Typography style={{ fontSize: '0.66rem', color: '#1e3a8a', lineHeight: 1.5 }}>
                      Para rotas já em <b>in_progress</b> (motorista a executar), o servidor faz
                      uma varredura <b>a cada 5 min</b>:<br />
                      <span style={{ display: 'inline-block', marginTop: 4 }}>
                        • Vai buscar incidentes <b>HERE</b> na bbox da rota<br />
                        • Filtra críticos no corredor de 500 m do trajecto<br />
                        • Quando aparece um <b>incidente NOVO</b> (não visto antes), emite
                        WebSocket <code>route:incident</code> para o tenant<br />
                        • O operador recebe um <b>toast</b> imediato + entrada permanente no sino
                        de notificações com o nome da rota, descrição do incidente e distância ao trajecto<br />
                        • Acidentes/vias-cortadas geram toast vermelho · obras geram toast amarelo
                      </span>
                    </Typography>
                  </Box>

                  {/* O que NÃO faz automaticamente */}
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                    Limitações conhecidas
                  </Typography>
                  <Box style={{ padding: '8px 10px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca' }}>
                    <Typography style={{ fontSize: '0.66rem', color: '#7f1d1d', lineHeight: 1.5 }}>
                      • O Planner <b>NÃO recalcula automaticamente</b> a rota quando detecta
                      incidentes — só avisa. Cabe ao operador decidir e usar o botão
                      "↻ Recalcular evitando esta zona".<br />
                      • O monitor server-side para rotas em trânsito usa <b>só HERE</b> (TomTom
                      fica para uma evolução futura). Adicionar TomTom duplicaria a frescura
                      mas adiciona custo de API.<br />
                      • O <b>Google Maps</b> não está acessível como fonte de incidentes —
                      a sua API pública não expõe esses dados (são do Waze e ficam reservados
                      à app própria do Google).<br />
                      • Obras e acidentes muito recentes podem demorar alguns minutos a
                      aparecer em qualquer um dos providers.
                    </Typography>
                  </Box>
                </Box>

                {/* Vehicle preset */}
                <Box className={classes.card}>
                  <Typography className={classes.cardTitle}>
                    <LocalShippingIcon style={{ fontSize: 16, color: '#475569' }} />
                    Veículo por defeito
                  </Typography>
                  <Typography className={classes.cardDesc}>
                    Cada preset aplica dimensões e peso típicos ao cálculo HERE. Os campos
                    abaixo servem apenas para substituir valores específicos.
                  </Typography>
                  <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
                    {VEHICLE_TYPES.map(v => {
                      const active = form.defaultVehicleSubType === v.value;
                      return (
                        <Box
                          key={v.value}
                          onClick={() => canEdit && setField('defaultVehicleSubType', v.value)}
                          style={{
                            padding: '10px 12px', borderRadius: 10,
                            cursor: canEdit ? 'pointer' : 'default',
                            background: active ? '#f8fafc' : '#fff',
                            border: `1px solid ${active ? '#94a3b8' : '#e2e8f0'}`,
                            transition: 'all 0.15s',
                          }}>
                          <Typography style={{
                            fontSize: '0.78rem', fontWeight: 500,
                            color: '#0f172a',
                          }}>
                            {v.label}
                          </Typography>
                          <Typography style={{ fontSize: '0.64rem', color: '#64748b', marginTop: 2 }}>
                            {v.weight}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  {/* Combustível por defeito (Gasóleo/Gasolina) — era só no diálogo de rota. */}
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 500, color: '#0f172a' }}>Combustível por defeito</Typography>
                    {[{ v: 'diesel', l: 'Gasóleo' }, { v: 'gasoline', l: 'Gasolina' }].map(({ v, l }) => {
                      const active = (form.defaultFuelType || 'diesel') === v;
                      return (
                        <Box key={v} onClick={() => canEdit && setField('defaultFuelType', v)}
                          style={{ padding: '5px 14px', borderRadius: 8, cursor: canEdit ? 'pointer' : 'default', fontSize: '0.74rem', fontWeight: 600,
                            background: active ? '#3b82f6' : '#fff', color: active ? '#fff' : '#64748b',
                            border: `1px solid ${active ? '#3b82f6' : '#e2e8f0'}` }}>
                          {l}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Route options */}
                <Box className={classes.card}>
                  <Typography className={classes.cardTitle}>
                    🛣 Opções de cálculo
                  </Typography>
                  <Typography className={classes.cardDesc}>
                    Restrições aplicadas à HERE quando calcula a rota.
                  </Typography>
                  {[
                    { key: 'showRouteAlternatives', label: 'Mostrar rotas alternativas', sub: 'Calcula e mostra alternativas no mapa (tracejado) ao planear a rota' },
                    { key: 'avoidTolls',     label: 'Evitar portagens',     sub: 'Rotas mais lentas mas sem custos de portagem' },
                    { key: 'avoidFerries',   label: 'Evitar ferries',       sub: 'Não usar travessias marítimas na rota' },
                    { key: 'avoidMotorways', label: 'Evitar auto-estradas', sub: 'Força uso de estradas nacionais e secundárias' },
                    { key: 'avoidTunnels',   label: 'Evitar túneis',        sub: 'Não usar túneis na rota' },
                    { key: 'avoidLEZ',       label: 'Evitar Zonas Baixas Emissões (LEZ)', sub: 'Zonas regulamentadas na Europa' },
                  ].filter(o => !(
                    // Fuel (Move Express/Transfuel) + país Angola: esconder
                    // evitar portagens/ferries/auto-estradas (não aplicável em AO).
                    // Continuam disponíveis no backoffice admin.
                    isMoveExpress && form.defaultCountry === 'AO' &&
                    ['avoidTolls', 'avoidFerries', 'avoidMotorways'].includes(o.key)
                  )).map(o => (
                    <Box key={o.key} className={classes.switchRow}>
                      <Box>
                        <Typography className={classes.switchLabel}>{o.label}</Typography>
                        <Typography className={classes.switchSub}>{o.sub}</Typography>
                      </Box>
                      <Switch
                        checked={!!form[o.key]}
                        onChange={e => setField(o.key, e.target.checked)}
                        disabled={!canEdit}
                        color="primary"
                      />
                    </Box>
                  ))}
                </Box>

                {/* Truck dimensions */}
                <Box className={classes.card} style={{ display: isMoveExpress ? 'none' : undefined }}>
                  <Typography className={classes.cardTitle}>
                    📐 Dimensões do camião (opcional)
                  </Typography>
                  <Typography className={classes.cardDesc}>
                    Deixa em branco para usar os valores do preset de veículo seleccionado acima.
                  </Typography>
                  <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                    {[
                      { key: 'truckWeight',   label: 'Peso (t)',       step: 0.1 },
                      { key: 'truckAxleLoad', label: 'Carga eixo (t)', step: 0.1 },
                      { key: 'truckHeight',   label: 'Altura (m)',     step: 0.05 },
                      { key: 'truckWidth',    label: 'Largura (m)',    step: 0.05 },
                      { key: 'truckLength',   label: 'Comprimento (m)',step: 0.1 },
                    ].map(f => (
                      <Box key={f.key}>
                        <Typography className={classes.fieldLabel}>{f.label}</Typography>
                        <TextField
                          type="number" size="small" variant="outlined" fullWidth
                          value={form[f.key]}
                          onChange={e => setField(f.key, e.target.value)}
                          disabled={!canEdit}
                          inputProps={{ min: 0, step: f.step }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* ─── Locais de partida por tipo de OT ─────────────────── */}
                <Box className={classes.card} style={{ display: isMoveExpress ? 'none' : undefined }}>
                  <Typography className={classes.cardTitle}>
                    🚚 Local de partida por tipo de OT
                  </Typography>
                  <Typography className={classes.cardDesc}>
                    Quando atribuis veículo a uma OT, o dropdown "Local de partida" abre pré-seleccionado com esta opção. O operador pode sempre mudar no momento do despacho.
                  </Typography>
                  {(() => {
                    // Só os tipos de OT que o PERFIL tem ativos no backoffice
                    // (allowedOrderTypes). Ex.: Casais (só delivery) vê apenas a
                    // "Entrega de mercadoria"; não faz sentido configurar partida
                    // para tipos que a organização não usa.
                    const orderTypes = [
                      { key: 'assistance', label: '🔧 Assistência técnica', hint: 'Petrotec PT/AO, intervenções no terreno' },
                      { key: 'delivery',   label: '📦 Entrega de mercadoria', hint: 'Recolha + entrega no cliente' },
                      { key: 'fuel',       label: '⛽ Distribuição de combustível', hint: 'Parque → depósito → cliente' },
                    ].filter(ot => !Array.isArray(form.allowedOrderTypes) || form.allowedOrderTypes.includes(ot.key));
                    const garages = (locations || []).filter(l => l.type === 'garage');
                    const warehouses = (locations || []).filter(l => l.type === 'warehouse');
                    const fuelDepots = (locations || []).filter(l => l.type === 'fuel_depot' || l.type === 'parque');
                    const others = (locations || []).filter(l => !['garage', 'warehouse', 'fuel_depot', 'parque'].includes(l.type));
                    const prefs = form.defaultDepartureByOrderType || {};
                    return (
                      <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {orderTypes.map(ot => {
                          const current = prefs[ot.key] || '';
                          return (
                            <Box key={ot.key} style={{
                              display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12,
                              alignItems: 'center',
                              padding: '10px 12px', background: '#fafbfc',
                              border: '1px solid #f1f5f9', borderRadius: 10,
                            }}>
                              <Box>
                                <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                                  {ot.label}
                                </Typography>
                                <Typography style={{ fontSize: '0.66rem', color: '#94a3b8', marginTop: 2 }}>
                                  {ot.hint}
                                </Typography>
                              </Box>
                              <FormControl size="small" variant="outlined" fullWidth disabled={!canEdit}>
                                <Select value={current}
                                  onChange={e => setField('defaultDepartureByOrderType', {
                                    ...prefs, [ot.key]: e.target.value,
                                  })}
                                  displayEmpty
                                  MenuProps={{ PaperProps: { style: { maxHeight: 420 } } }}>
                                  <MenuItem value="">
                                    <span style={{ color: '#64748b', fontSize: '0.78rem' }}>— Auto —</span>
                                  </MenuItem>
                                  <MenuItem value="driver-home" style={{ fontSize: '0.78rem' }}>
                                    🏠 Casa do motorista
                                  </MenuItem>
                                  <MenuItem value="vehicle-gps" style={{ fontSize: '0.78rem' }}>
                                    🚗 Posição GPS do veículo
                                  </MenuItem>
                                  {garages.length > 0 && [
                                    <MenuItem key="hdr-g" disabled style={{ fontSize: '0.6rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', background: '#f8fafc', opacity: 1 }}>
                                      🏢 Garagens / Oficinas
                                    </MenuItem>,
                                    ...garages.map(l => (
                                      <MenuItem key={`g-${l.id}`} value={`favorite:${l.id}`} style={{ fontSize: '0.78rem', paddingLeft: 20 }}>
                                        🏢 {l.name}
                                      </MenuItem>
                                    )),
                                  ]}
                                  {warehouses.length > 0 && [
                                    <MenuItem key="hdr-w" disabled style={{ fontSize: '0.6rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', background: '#f8fafc', opacity: 1 }}>
                                      🏬 Armazéns
                                    </MenuItem>,
                                    ...warehouses.map(l => (
                                      <MenuItem key={`w-${l.id}`} value={`favorite:${l.id}`} style={{ fontSize: '0.78rem', paddingLeft: 20 }}>
                                        🏬 {l.name}
                                      </MenuItem>
                                    )),
                                  ]}
                                  {fuelDepots.length > 0 && [
                                    <MenuItem key="hdr-f" disabled style={{ fontSize: '0.6rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', background: '#f8fafc', opacity: 1 }}>
                                      ⛽ Parques / Depósitos
                                    </MenuItem>,
                                    ...fuelDepots.map(l => (
                                      <MenuItem key={`f-${l.id}`} value={`favorite:${l.id}`} style={{ fontSize: '0.78rem', paddingLeft: 20 }}>
                                        ⛽ {l.name}
                                      </MenuItem>
                                    )),
                                  ]}
                                  {others.length > 0 && [
                                    <MenuItem key="hdr-o" disabled style={{ fontSize: '0.6rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', background: '#f8fafc', opacity: 1 }}>
                                      📍 Outros favoritos
                                    </MenuItem>,
                                    ...others.map(l => (
                                      <MenuItem key={`o-${l.id}`} value={`favorite:${l.id}`} style={{ fontSize: '0.78rem', paddingLeft: 20 }}>
                                        📍 {l.name}
                                      </MenuItem>
                                    )),
                                  ]}
                                </Select>
                              </FormControl>
                            </Box>
                          );
                        })}
                        <Box style={{
                          padding: '10px 12px', background: '#f0f9ff',
                          border: '1px solid #e0f2fe', borderRadius: 10,
                          fontSize: '0.7rem', color: '#0c4a6e', lineHeight: 1.5,
                        }}>
                          <b>💡 Como funciona:</b> O valor escolhido aqui aparece como default no dropdown ao atribuir veículo. Se a opção configurada não estiver disponível no momento (motorista sem morada, veículo sem GPS, favorito apagado), cai automaticamente no fallback Auto.
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              </>
            )}

            {/* ═════ Fuel prices by country ═════ */}
            {section === 'fuel' && (() => {
              const entries = Object.entries(form.fuelPricesByCountry || {});
              const countryCount = entries.length;
              const dieselCount = entries.filter(([, p]) => p?.diesel != null && p.diesel > 0).length;
              const gasolineCount = entries.filter(([, p]) => p?.gasoline != null && p.gasoline > 0).length;
              const availableCountries = FUEL_COUNTRIES.filter(c => !(form.fuelPricesByCountry || {})[c.code]);

              return (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {/* Header — soft sky gradient with KPIs */}
                  <Box style={{
                    background: 'linear-gradient(135deg, #60a5fa 0%, #93c5fd 60%, #bae6fd 100%)',
                    borderRadius: 14, padding: '22px 26px', color: '#fff',
                    boxShadow: '0 8px 24px -10px rgba(96, 165, 250, 0.45)',
                    display: 'flex', alignItems: 'center', gap: 22,
                  }}>
                    <Box style={{
                      width: 56, height: 56, borderRadius: 14,
                      background: 'rgba(255,255,255,0.18)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backdropFilter: 'blur(4px)',
                    }}>
                      <LocalGasStationIcon style={{ fontSize: 30 }} />
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{ fontSize: '1rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
                        Tarifário de combustível
                      </Typography>
                      <Typography style={{ fontSize: '0.74rem', opacity: 0.85, marginTop: 3, lineHeight: 1.5 }}>
                        Preços oficiais por país, na moeda local. Sobrepõem o EU Oil Bulletin no cálculo de custos por rota.
                      </Typography>
                    </Box>
                    <Box style={{ display: 'flex', gap: 14 }}>
                      {[
                        { label: 'Países', value: countryCount },
                        { label: 'Gasóleo', value: `${dieselCount}/${countryCount || 0}` },
                        { label: 'Gasolina', value: `${gasolineCount}/${countryCount || 0}` },
                      ].map(kpi => (
                        <Box key={kpi.label} style={{
                          background: 'rgba(255,255,255,0.12)', borderRadius: 10,
                          padding: '8px 14px', textAlign: 'center', minWidth: 70,
                          border: '1px solid rgba(255,255,255,0.15)',
                        }}>
                          <Typography style={{ fontSize: '1.2rem', fontWeight: 800, lineHeight: 1 }}>
                            {kpi.value}
                          </Typography>
                          <Typography style={{ fontSize: '0.58rem', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>
                            {kpi.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Pricing table */}
                  <Box style={{
                    background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0',
                    overflow: 'hidden', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
                  }}>
                    {countryCount === 0 ? (
                      <Box style={{
                        padding: '48px 24px', textAlign: 'center',
                        background: 'radial-gradient(circle at center, #f8fafc 0%, #fff 70%)',
                      }}>
                        <Box style={{
                          width: 64, height: 64, margin: '0 auto 12px',
                          borderRadius: 16, background: '#f1f5f9',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <LocalGasStationIcon style={{ fontSize: 32, color: '#94a3b8' }} />
                        </Box>
                        <Typography style={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                          Sem preços configurados
                        </Typography>
                        <Typography style={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.6, maxWidth: 380, margin: '0 auto' }}>
                          Hoje as rotas usam o EU Oil Bulletin para países europeus e o default europeu de 1,65 €/L para os restantes. Adiciona um país abaixo para tomar controlo dos preços.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {/* Table header */}
                        <Box style={{
                          display: 'grid', gridTemplateColumns: '2fr 1.3fr 1.3fr 0.8fr 40px',
                          padding: '14px 22px', background: '#fafbfc',
                          borderBottom: '2px solid #e2e8f0',
                          fontSize: '0.62rem', fontWeight: 800, color: '#64748b',
                          textTransform: 'uppercase', letterSpacing: '0.08em',
                          alignItems: 'center', gap: 14,
                        }}>
                          <span>País</span>
                          <span style={{ textAlign: 'right' }}>⛽ Gasóleo</span>
                          <span style={{ textAlign: 'right' }}>⛽ Gasolina</span>
                          <span style={{ textAlign: 'center' }}>Moeda</span>
                          <span></span>
                        </Box>

                        {/* Table rows */}
                        {entries.map(([cc, prices], idx) => {
                          const meta = FUEL_COUNTRIES.find(c => c.code === cc) || { name: cc, currency: 'EUR' };
                          const sym = CURRENCY_SYMBOLS[meta.currency] || meta.currency;
                          const isLast = idx === entries.length - 1;
                          return (
                            <Box key={cc} style={{
                              display: 'grid', gridTemplateColumns: '2fr 1.3fr 1.3fr 0.8fr 40px',
                              padding: '14px 22px', alignItems: 'center', gap: 14,
                              borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                              transition: 'background 0.15s',
                              background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                            }}>
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Box style={{
                                  width: 36, height: 28, borderRadius: 6,
                                  background: '#f1f5f9',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '1.2rem',
                                  border: '1px solid #e2e8f0',
                                }}>
                                  {flagFromIso2(cc)}
                                </Box>
                                <Box>
                                  <Typography style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                    {meta.name}
                                  </Typography>
                                  <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, marginTop: 2 }}>
                                    {cc} · {meta.currency}
                                  </Typography>
                                </Box>
                              </Box>
                              <TextField size="small" variant="outlined" type="number"
                                value={prices?.diesel ?? ''}
                                onChange={e => {
                                  const v = e.target.value === '' ? null : Number(e.target.value);
                                  setField('fuelPricesByCountry', {
                                    ...form.fuelPricesByCountry,
                                    [cc]: { ...(prices || {}), diesel: v },
                                  });
                                }}
                                InputProps={{
                                  endAdornment: <Typography style={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 600, marginLeft: 4 }}>{sym}/L</Typography>,
                                  style: { background: '#fff' },
                                }}
                                inputProps={{ min: 0, step: 0.01, style: { fontSize: '0.86rem', textAlign: 'right', fontWeight: 600, padding: '8px 10px' } }}
                                placeholder="—"
                                disabled={!canEdit}
                              />
                              <TextField size="small" variant="outlined" type="number"
                                value={prices?.gasoline ?? ''}
                                onChange={e => {
                                  const v = e.target.value === '' ? null : Number(e.target.value);
                                  setField('fuelPricesByCountry', {
                                    ...form.fuelPricesByCountry,
                                    [cc]: { ...(prices || {}), gasoline: v },
                                  });
                                }}
                                InputProps={{
                                  endAdornment: <Typography style={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 600, marginLeft: 4 }}>{sym}/L</Typography>,
                                  style: { background: '#fff' },
                                }}
                                inputProps={{ min: 0, step: 0.01, style: { fontSize: '0.86rem', textAlign: 'right', fontWeight: 600, padding: '8px 10px' } }}
                                placeholder="—"
                                disabled={!canEdit}
                              />
                              <Box style={{ textAlign: 'center' }}>
                                <Chip label={meta.currency} size="small"
                                  style={{
                                    height: 22, fontSize: '0.66rem', fontWeight: 700,
                                    background: '#f0f9ff', color: '#0369a1',
                                    border: '1px solid #e0f2fe',
                                  }} />
                              </Box>
                              <Tooltip title="Remover">
                                <span>
                                  <IconButton size="small" disabled={!canEdit}
                                    onClick={() => {
                                      const next = { ...form.fuelPricesByCountry };
                                      delete next[cc];
                                      setField('fuelPricesByCountry', next);
                                    }}
                                    style={{ padding: 6 }}>
                                    <DeleteOutlineIcon style={{ fontSize: 18, color: '#ef4444' }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          );
                        })}
                      </>
                    )}

                    {/* Add country footer */}
                    {availableCountries.length > 0 && (
                      <Box style={{
                        padding: '14px 22px',
                        borderTop: countryCount > 0 ? '1px solid #f1f5f9' : 'none',
                        background: countryCount === 0 ? '#fafbfc' : '#fff',
                        display: 'flex', alignItems: 'center', gap: 12,
                      }}>
                        <AddIcon style={{ fontSize: 18, color: '#3b82f6' }} />
                        <FormControl size="small" variant="outlined" style={{ flex: 1 }} disabled={!canEdit}>
                          <InputLabel>Adicionar país</InputLabel>
                          <Select label="Adicionar país" value=""
                            onChange={e => {
                              const cc = e.target.value;
                              if (!cc) return;
                              const existing = form.fuelPricesByCountry || {};
                              if (existing[cc]) return;
                              setField('fuelPricesByCountry', { ...existing, [cc]: { diesel: null, gasoline: null } });
                            }}
                            MenuProps={{ PaperProps: { style: { maxHeight: 480 } } }}>
                            {/* Agrupados por região para navegação em 60+ países */}
                            {['Europa', 'África'].flatMap(region => {
                              const regionItems = availableCountries.filter(c => c.region === region);
                              if (regionItems.length === 0) return [];
                              return [
                                <MenuItem key={`hdr-${region}`} disabled style={{
                                  fontSize: '0.62rem', fontWeight: 800, color: '#64748b',
                                  textTransform: 'uppercase', letterSpacing: '0.08em',
                                  background: '#f8fafc', opacity: 1,
                                }}>
                                  {region === 'Europa' ? '🇪🇺' : '🌍'} &nbsp;{region}
                                </MenuItem>,
                                ...regionItems.map(c => (
                                  <MenuItem key={c.code} value={c.code} style={{ fontSize: '0.82rem', paddingLeft: 20 }}>
                                    <span style={{ marginRight: 10, fontSize: '1rem' }}>{flagFromIso2(c.code)}</span>
                                    {c.name}
                                    <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: '0.74rem' }}>· {c.currency}</span>
                                  </MenuItem>
                                )),
                              ];
                            })}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Box>

                  {/* Hierarchy explainer */}
                  <Box style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0', borderRadius: 14,
                    padding: '18px 22px',
                    display: isMoveExpress ? 'none' : undefined,
                  }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <Box style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography style={{ fontSize: '0.95rem' }}>💡</Typography>
                      </Box>
                      <Typography style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f172a' }}>
                        Hierarquia de preços ao calcular uma rota
                      </Typography>
                    </Box>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { num: 1, title: 'Preço que mudaste à mão para esta rota', sub: 'Quando clicas no preço dentro do card "Combustível por País" no painel direito da rota, é um ajuste pontual só dela.' },
                        { num: 2, title: 'Preço definido aqui nas Preferências', sub: 'É a opção recomendada para o teu dia-a-dia: defines uma vez por país e o sistema usa em todas as rotas.', highlight: true },
                        { num: 3, title: 'Preço do campo "Combustível" da rota', sub: 'O valor que colocas na sidebar quando crias a rota. Só conta se a rota toda passar num único país.' },
                        { num: 4, title: 'Preço oficial europeu (automático)', sub: 'A Comissão Europeia publica preços semanais. O sistema aplica-os sozinho quando não há nada definido aqui (só funciona em países da UE).' },
                        { num: 5, title: 'Valor médio europeu (último recurso)', sub: 'Se nenhum dos anteriores estiver disponível, usa 1,65 €/L para gasóleo e 1,75 €/L para gasolina.' },
                      ].map(step => (
                        <Box key={step.num} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 12,
                          padding: '10px 12px',
                          background: step.highlight ? '#f0f9ff' : 'transparent',
                          border: step.highlight ? '1px solid #e0f2fe' : '1px solid transparent',
                          borderRadius: 10,
                        }}>
                          <Box style={{
                            width: 24, height: 24, borderRadius: 6,
                            background: step.highlight ? '#60a5fa' : '#f1f5f9',
                            color: step.highlight ? '#fff' : '#475569',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.72rem', fontWeight: 800, flexShrink: 0,
                          }}>
                            {step.num}
                          </Box>
                          <Box style={{ minWidth: 0 }}>
                            <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: step.highlight ? '#0c4a6e' : '#0f172a' }}>
                              {step.title}
                            </Typography>
                            <Typography style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2, lineHeight: 1.45 }}>
                              {step.sub}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              );
            })()}

            {/* ═════ Crossing fares (ferries + Eurotúnel) ═════
                Mesma secção do tab "Combustível" porque são ambas
                tabelas de preços que afectam o cálculo do custo da rota.
                HERE não devolve estes valores — admin do tenant edita
                quando os preços reais mudam (1-2x/ano). */}
            {section === 'fuel' && (() => {
              const CROSSINGS = [
                { key: 'eurotunnel',                name: 'Eurotúnel Folkestone-Calais',  icon: '🚇' },
                { key: 'ferry_holyhead_dublin',     name: 'Ferry Holyhead-Dublin',           icon: '⛴️' },
                { key: 'ferry_cairnryan_belfast',   name: 'Ferry Cairnryan-Belfast',         icon: '⛴️' },
                { key: 'ferry_dublin_cherbourg',    name: 'Ferry Dublin/Rosslare-Cherbourg', icon: '⛴️' },
                { key: 'ferry_generic',             name: 'Outras travessias (genérico)',  icon: '⛴️' },
              ];
              const DEFAULTS = {
                eurotunnel:              { car: 175, light_truck: 290, truck_12m: 450, truck_18m: 720 },
                ferry_holyhead_dublin:   { car: 100, light_truck: 220, truck_12m: 380, truck_18m: 540 },
                ferry_cairnryan_belfast: { car: 95,  light_truck: 210, truck_12m: 360, truck_18m: 520 },
                ferry_dublin_cherbourg:  { car: 150, light_truck: 320, truck_12m: 520, truck_18m: 780 },
                ferry_generic:           { car: 80,  light_truck: 180, truck_12m: 300, truck_18m: 450 },
              };
              const VEHICLE_COLS = [
                { key: 'car',         label: '🚗 Carro' },
                { key: 'light_truck', label: '🚚 Ligeiro (≤7,5t)' },
                { key: 'truck_12m',   label: '🚛 Camião (12-18t)' },
                { key: 'truck_18m',   label: '🛢 Cisterna (>18t)' },
              ];
              const prefs = form.crossingFares || {};
              return (
                <Box style={{ marginTop: 18, display: isMoveExpress ? 'none' : undefined }}>
                  <Box style={{
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 60%, #bae6fd 100%)',
                    borderRadius: 14, padding: '20px 24px', color: '#fff',
                    boxShadow: '0 8px 24px -10px rgba(14, 165, 233, 0.45)',
                    display: 'flex', alignItems: 'center', gap: 18, marginBottom: 16,
                  }}>
                    <Box style={{
                      width: 50, height: 50, borderRadius: 12,
                      background: 'rgba(255,255,255,0.18)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 24,
                    }}>⛴️</Box>
                    <Box style={{ flex: 1 }}>
                      <Typography style={{ fontSize: '1rem', fontWeight: 800 }}>
                        Travessias marítimas / Eurotúnel
                      </Typography>
                      <Typography style={{ fontSize: '0.74rem', opacity: 0.9, marginTop: 3, lineHeight: 1.5 }}>
                        Tarifas por categoria de veículo. HERE não devolve estes valores; aqui defines o que o sistema soma quando a rota atravessa estas zonas.
                      </Typography>
                    </Box>
                  </Box>

                  <Box style={{
                    background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden',
                  }}>
                    {/* Header */}
                    <Box style={{
                      display: 'grid', gridTemplateColumns: '2fr repeat(4, 1fr) 32px',
                      padding: '12px 18px', background: '#fafbfc',
                      borderBottom: '2px solid #e2e8f0',
                      fontSize: '0.6rem', fontWeight: 800, color: '#64748b',
                      textTransform: 'uppercase', letterSpacing: '0.08em', alignItems: 'center', gap: 10,
                    }}>
                      <span>Travessia</span>
                      {VEHICLE_COLS.map(v => (
                        <span key={v.key} style={{ textAlign: 'right' }}>{v.label}</span>
                      ))}
                      <span></span>
                    </Box>
                    {CROSSINGS.map((c, idx) => {
                      const row = prefs[c.key] || {};
                      const isCustom = VEHICLE_COLS.some(v => row[v.key] != null);
                      return (
                        <Box key={c.key} style={{
                          display: 'grid', gridTemplateColumns: '2fr repeat(4, 1fr) 32px',
                          padding: '12px 18px', alignItems: 'center', gap: 10,
                          borderBottom: idx === CROSSINGS.length - 1 ? 'none' : '1px solid #f1f5f9',
                          background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                        }}>
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Box style={{
                              width: 32, height: 24, borderRadius: 6, background: '#f0f9ff',
                              border: '1px solid #e0f2fe', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem',
                            }}>{c.icon}</Box>
                            <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>{c.name}</Typography>
                            {isCustom && (
                              <Chip label="custom" size="small"
                                style={{ height: 16, fontSize: '0.55rem', fontWeight: 700, background: '#dbeafe', color: '#1e40af' }} />
                            )}
                          </Box>
                          {VEHICLE_COLS.map(v => (
                            <TextField key={v.key} size="small" variant="outlined" type="number"
                              value={row[v.key] ?? ''}
                              onChange={e => {
                                const val = e.target.value === '' ? null : Number(e.target.value);
                                const nextRow = { ...row };
                                if (val == null) delete nextRow[v.key];
                                else nextRow[v.key] = val;
                                const nextPrefs = { ...prefs };
                                if (Object.keys(nextRow).length === 0) delete nextPrefs[c.key];
                                else nextPrefs[c.key] = nextRow;
                                setField('crossingFares', nextPrefs);
                              }}
                              InputProps={{
                                endAdornment: <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600 }}>€</Typography>,
                                style: { background: '#fff' },
                              }}
                              inputProps={{ min: 0, step: 1, style: { fontSize: '0.82rem', textAlign: 'right', fontWeight: 600, padding: '8px 10px' } }}
                              placeholder={String(DEFAULTS[c.key]?.[v.key] || '')}
                              disabled={!canEdit}
                            />
                          ))}
                          <Tooltip title="Repor defaults">
                            <span>
                              <IconButton size="small" disabled={!canEdit}
                                onClick={() => {
                                  const nextPrefs = { ...prefs };
                                  delete nextPrefs[c.key];
                                  setField('crossingFares', nextPrefs);
                                }}>
                                <DeleteOutlineIcon style={{ fontSize: 18, color: '#ef4444' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>

                  <Box style={{
                    marginTop: 12, padding: '12px 14px',
                    background: '#f0f9ff', border: '1px solid #e0f2fe',
                    borderRadius: 10, fontSize: '0.72rem', color: '#0c4a6e', lineHeight: 1.5,
                  }}>
                    <b>💡 Como funciona:</b> os campos vazios usam os valores default em <i>placeholder</i>. Quando edites um valor, vira <b>custom</b> e é esse que o sistema usa em todos os cálculos de portagens. Óptimo para actualizar tarifas Eurotúnel quando mudam (~1×/ano).
                  </Box>
                </Box>
              );
            })()}

            {/* ═════ Chat (Fase 2) — atalhos e notificações ═════ */}
            {section === 'chat' && (
              <Box className={classes.card} style={{ display: isMoveExpress ? 'none' : undefined }}>
                <Typography className={classes.cardTitle}>
                  <ChatBubbleOutlineIcon style={{ fontSize: 16, color: '#4f46e5' }} />
                  Respostas rápidas
                </Typography>
                <Typography style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 12 }}>
                  Frases pré-feitas que aparecem como atalhos no chat. Click insere o texto no input — o operador pode rever antes de enviar. Máximo 8 frases.
                </Typography>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(form.chatQuickReplies || []).map((reply, idx) => (
                    <Box key={idx} style={{ display: 'flex', gap: 6 }}>
                      <TextField
                        size="small" variant="outlined" fullWidth
                        value={reply}
                        onChange={e => {
                          const next = [...(form.chatQuickReplies || [])];
                          next[idx] = e.target.value;
                          setField('chatQuickReplies', next);
                        }}
                        inputProps={{ maxLength: 100, style: { fontSize: '0.78rem' } }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const next = (form.chatQuickReplies || []).filter((_, i) => i !== idx);
                          setField('chatQuickReplies', next);
                        }}
                        style={{ background: '#fee2e2' }}
                      >
                        <CloseIcon style={{ fontSize: 14, color: '#dc2626' }} />
                      </IconButton>
                    </Box>
                  ))}
                  {(form.chatQuickReplies || []).length < 8 && (
                    <Button
                      size="small"
                      onClick={() => setField('chatQuickReplies', [...(form.chatQuickReplies || []), ''])}
                      style={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.72rem', color: '#4f46e5' }}
                    >
                      + Adicionar frase
                    </Button>
                  )}
                </Box>

                <Box style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.chatNotificationSound !== false}
                        onChange={e => setField('chatNotificationSound', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                          Som de notificação
                        </Typography>
                        <Typography style={{ fontSize: '0.7rem', color: '#64748b' }}>
                          Beep curto quando chega mensagem do motorista (e o chat está fechado).
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>
            )}

            {/* ═════ Notificações (Fase C, perfil combustível) ═════ */}
            {section === 'notifications' && (
              <Box className={classes.card}>
                <NotificationRecipients lang="pt" />
              </Box>
            )}

            {/* ═════ Favorites ═════ */}
            {section === 'favorites' && (
              <Box className={classes.card}>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <Typography className={classes.cardTitle} style={{ marginBottom: 0 }}>
                    <StarIcon style={{ fontSize: 16, color: '#eab308' }} />
                    Locais guardados
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
                    onClick={() => { dispatch(setActiveView('favorites')); onClose(); }}
                    style={{ textTransform: 'none', fontSize: '0.74rem', fontWeight: 500 }}
                  >
                    Gerir favoritos
                  </Button>
                </Box>
                <Typography className={classes.cardDesc}>
                  Locais frequentemente usados como origem, destino ou paragem intermédia.
                  Podes criar novos a partir do formulário de OT ou de rota.
                </Typography>
                {locations.length === 0 ? (
                  <Box style={{ padding: 30, textAlign: 'center', background: '#f8fafc', borderRadius: 8 }}>
                    <StarBorderIcon style={{ fontSize: 32, color: '#cbd5e1', marginBottom: 8 }} />
                    <Typography style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                      Sem locais guardados.
                    </Typography>
                  </Box>
                ) : (
                  <Box style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 380, overflow: 'auto' }}>
                    {locations.slice(0, 50).map(loc => (
                      <Box key={loc.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', borderRadius: 8,
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                      }}>
                        <Box style={{
                          width: 28, height: 28, borderRadius: 8,
                          background: loc.isPreferred ? '#fef3c7' : '#f1f5f9',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {loc.isPreferred
                            ? <StarIcon style={{ fontSize: 14, color: '#eab308' }} />
                            : <RoomIcon style={{ fontSize: 14, color: '#94a3b8' }} />}
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Typography style={{ fontSize: '0.8rem', fontWeight: 500, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {loc.name}
                          </Typography>
                          {loc.address && (
                            <Typography style={{ fontSize: '0.66rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {loc.address}
                            </Typography>
                          )}
                        </Box>
                        <Box style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
                          {loc.category && (
                            <Chip label={loc.category.name} size="small" style={{ height: 20, fontSize: '0.6rem', fontWeight: 700, background: `${loc.category.color}1a`, color: loc.category.color, border: `1px solid ${loc.category.color}` }} />
                          )}
                          <Chip label={locTypeLabel('pt', loc.type)} size="small" style={{ height: 20, fontSize: '0.6rem', fontWeight: 500, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }} />
                          <Tooltip title="Editar favorito">
                            <IconButton size="small"
                              onClick={() => {
                                // Abre o gestor de favoritos já na edição deste favorito
                                // (evita "Gerir favoritos" → procurar → editar).
                                dispatch(setActiveView('favorites'));
                                onClose();
                                setTimeout(() => window.dispatchEvent(new CustomEvent('favoritesOpenEdit', { detail: { loc } })), 60);
                              }}
                              style={{ padding: 4 }}>
                              <EditIcon style={{ fontSize: 16, color: '#64748b' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                    {locations.length > 50 && (
                      <Typography style={{ fontSize: '0.68rem', color: '#94a3b8', textAlign: 'center', padding: '6px 0' }}>
                        +{locations.length - 50} — abrir gestor para ver todos
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box className={classes.footer}>
            <Box className={classes.audit}>
              {profile?.updatedAt && (
                <>
                  <HistoryIcon style={{ fontSize: 13 }} />
                  Última alteração: {new Date(profile.updatedAt).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </>
              )}
            </Box>
            <Box className={classes.footerActions}>
              <Button
                onClick={onClose}
                style={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}>
                Fechar
              </Button>
              {canEdit && section !== 'favorites' && section !== 'notifications' && (
                <Button
                  variant="contained"
                  className={classes.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <CheckCircleIcon style={{ fontSize: 16 }} />}
                >
                  {saving ? 'A guardar…' : 'Guardar alterações'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
